/* eslint-disable react-refresh/only-export-components -- context + hook pattern */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

type ResourcesContextValue = {
  getResourceValue: (key: string, fallback?: string) => string;
  isLoading: boolean;
};

const ResourcesContext = createContext<ResourcesContextValue | undefined>(undefined);

type ResourcesProviderProps = {
  children: React.ReactNode;
};

const RESOURCES_API = import.meta.env.VITE_RESOURCES_API_URL ?? "";

export function ResourcesProvider({ children }: ResourcesProviderProps) {
  const [resources, setResources] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadResources() {
      if (!RESOURCES_API.trim()) {
        if (isMounted) setIsLoading(false);
        return;
      }
      try {
        const lang = "en";
        const signedUrlRes = await fetch(
          `${RESOURCES_API}?lang=${encodeURIComponent(lang)}&platform=web&appName=support_chat`,
          { cache: "no-store" }
        );
        if (!signedUrlRes.ok) {
          throw new Error(`Signed URL fetch failed: ${signedUrlRes.status} ${signedUrlRes.statusText}`);
        }
        const signedUrl = (await signedUrlRes.text()).trim();
        if (!signedUrl) throw new Error("Empty signed URL");

        const response = await fetch(signedUrl, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Resource fetch failed: ${response.status} ${response.statusText}`);
        }
        const json = await response.json();
        if (isMounted && json && typeof json === "object") {
          setResources(json as Record<string, string>);
          setIsLoading(false);
        } else if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error loading resources:", err);
        if (isMounted) setIsLoading(false);
      }
    }
    loadResources();
    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo<ResourcesContextValue>(
    () => ({
      getResourceValue: (key: string, fallback?: string) => resources[key] ?? fallback ?? key,
      isLoading,
    }),
    [resources, isLoading]
  );

  return <ResourcesContext.Provider value={value}>{children}</ResourcesContext.Provider>;
}

export function useResourcesContext(): ResourcesContextValue {
  const ctx = useContext(ResourcesContext);
  if (!ctx) {
    return {
      getResourceValue: (key: string, fallback?: string) => fallback ?? key,
      isLoading: false,
    };
  }
  return ctx;
}
